import React, { useState, useEffect } from 'react';
import { SUPPORTED_LANGUAGES, setAppLanguage, AppLanguage } from '../i18n';
import Layout from '../components/Layout';
import ChangePasswordCard from '../components/ChangePasswordCard';
import ToastContainer from '../components/ToastContainer';
import { useVendor } from '../contexts/VendorContext';
import { apiFetch } from '../utils/apiClient';

const LANGUAGE_NAMES: Record<string, string> = { en: 'English', si: 'සිංහල', ta: 'தமிழ்' };

interface ToastData {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

interface ProfileFormData {
  // User fields
  first_name: string;
  last_name: string;
  email: string;
  // Vendor fields
  name: string;
  contact_email: string;
  contact_phone: string;
  address: string;
  profile_picture: string;
  business_name: string;
  bio: string;
  city: string;
  country: string;
  bank_name: string;
  bank_account_name: string;
  bank_account_number: string;
  bank_branch: string;
  shopify_vendor_name: string;
  preferred_language: string;
}

const Profile: React.FC = () => {
  const { user, vendor, fetchProfile } = useVendor();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const [formData, setFormData] = useState<ProfileFormData>({
    first_name: '',
    last_name: '',
    email: '',
    name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    profile_picture: '',
    business_name: '',
    bio: '',
    city: '',
    country: '',
    bank_name: '',
    bank_account_name: '',
    bank_account_number: '',
    bank_branch: '',
    shopify_vendor_name: '',
    preferred_language: 'en',
  });

  useEffect(() => {
    if (user && vendor) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        name: vendor.name || '',
        contact_email: vendor.contact_email || '',
        contact_phone: vendor.contact_phone || '',
        address: vendor.address || '',
        profile_picture: vendor.profile_picture || '',
        business_name: vendor.business_name || '',
        bio: vendor.bio || '',
        city: vendor.city || '',
        country: vendor.country || '',
        bank_name: vendor.bank_name || '',
        bank_account_name: vendor.bank_account_name || '',
        bank_account_number: vendor.bank_account_number || '',
        bank_branch: vendor.bank_branch || '',
        shopify_vendor_name: vendor.shopify_vendor_name || '',
        preferred_language: vendor.preferred_language || 'en',
      });
    }
  }, [user, vendor]);

  const addToast = (message: string, type: ToastData['type']) => {
    const newToast: ToastData = { id: Date.now().toString(), message, type };
    setToasts((prev) => [...prev, newToast]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await apiFetch('/api/user/profile/', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user: {
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
          },
          vendor: {
            name: formData.name,
            contact_email: formData.contact_email,
            contact_phone: formData.contact_phone,
            address: formData.address,
            profile_picture: formData.profile_picture,
            business_name: formData.business_name,
            bio: formData.bio,
            city: formData.city,
            country: formData.country,
            bank_name: formData.bank_name,
            bank_account_name: formData.bank_account_name,
            bank_account_number: formData.bank_account_number,
            bank_branch: formData.bank_branch,
            preferred_language: formData.preferred_language,
          },
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || JSON.stringify(err));
      }

      await fetchProfile();
      setIsEditing(false);
      addToast('Profile updated successfully!', 'success');
    } catch (err: any) {
      addToast(err.message || 'Failed to update profile', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form data
    if (user && vendor) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        name: vendor.name || '',
        contact_email: vendor.contact_email || '',
        contact_phone: vendor.contact_phone || '',
        address: vendor.address || '',
        profile_picture: vendor.profile_picture || '',
        business_name: vendor.business_name || '',
        bio: vendor.bio || '',
        city: vendor.city || '',
        country: vendor.country || '',
        bank_name: vendor.bank_name || '',
        bank_account_name: vendor.bank_account_name || '',
        bank_account_number: vendor.bank_account_number || '',
        bank_branch: vendor.bank_branch || '',
        shopify_vendor_name: vendor.shopify_vendor_name || '',
        preferred_language: vendor.preferred_language || 'en',
      });
    }
  };

  if (!user) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-600">Please log in to view your profile.</p>
        </div>
      </Layout>
    );
  }

  const initials =
    (user.first_name?.[0] || user.username[0] || '').toUpperCase() +
    (user.last_name?.[0] || '').toUpperCase();

  return (
    <Layout>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <div className="w-full max-w-3xl mx-auto">
        {/* Profile Header */}
        <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 h-32 sm:h-40" />
          <div className="px-4 sm:px-6 pb-6 -mt-12 sm:-mt-16 flex flex-col sm:flex-row items-center sm:items-end gap-4">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {formData.profile_picture ? (
                <img
                  src={formData.profile_picture}
                  alt={formData.name}
                  className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-white object-cover bg-white shadow"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-white bg-blue-100 flex items-center justify-center shadow">
                  <span className="text-3xl sm:text-4xl font-bold text-blue-600">
                    {initials}
                  </span>
                </div>
              )}
            </div>
            <div className="text-center sm:text-left flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                {formData.business_name || formData.name || user.username}
              </h1>
              <p className="text-sm text-gray-500">
                @{user.username}
                {formData.city && ` · ${formData.city}`}
                {formData.country && `, ${formData.country}`}
              </p>
            </div>
            <button
              onClick={() => (isEditing ? handleCancel() : setIsEditing(true))}
              className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium active:bg-gray-50 min-h-[44px] touch-manipulation"
            >
              {isEditing ? 'Cancel' : 'Edit Profile'}
            </button>
          </div>
        </div>

        {/* Stats */}
        {vendor && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white shadow rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">
                {vendor.inventory_items_count ?? 0}
              </p>
              <p className="text-xs sm:text-sm text-gray-500">Books Listed</p>
            </div>
            <div className="bg-white shadow rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-600">
                {vendor.is_active ? 'Active' : 'Inactive'}
              </p>
              <p className="text-xs sm:text-sm text-gray-500">Account Status</p>
            </div>
            <div className="bg-white shadow rounded-lg p-4 text-center col-span-2 sm:col-span-1">
              <p className="text-2xl font-bold text-gray-700">
                {vendor.commission_rate
                  ? `${vendor.commission_rate}%`
                  : 'N/A'}
              </p>
              <p className="text-xs sm:text-sm text-gray-500">Commission Rate</p>
            </div>
          </div>
        )}

        {/* Profile Details */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Profile Details</h2>
          </div>

          <div className="px-4 sm:px-6 py-4 space-y-6">
            {/* Personal Information */}
            <section>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Personal Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  editing={isEditing}
                  onChange={handleChange}
                />
                <Field
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  editing={isEditing}
                  onChange={handleChange}
                />
                <Field
                  label="Email"
                  name="email"
                  value={formData.email}
                  editing={isEditing}
                  onChange={handleChange}
                  type="email"
                />
                <Field
                  label="Phone"
                  name="contact_phone"
                  value={formData.contact_phone}
                  editing={isEditing}
                  onChange={handleChange}
                  type="tel"
                />
              </div>
            </section>

            {/* Business Information */}
            <section>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Business Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="Store Name"
                  name="name"
                  value={formData.name}
                  editing={false}
                  onChange={handleChange}
                />
                <Field
                  label="Business Name"
                  name="business_name"
                  value={formData.business_name}
                  editing={isEditing}
                  onChange={handleChange}
                />
                <Field
                  label="BookMarketLk Seller Name"
                  name="shopify_vendor_name"
                  value={formData.shopify_vendor_name}
                  editing={false}
                  onChange={handleChange}
                />
                <Field
                  label="Contact Email"
                  name="contact_email"
                  value={formData.contact_email}
                  editing={isEditing}
                  onChange={handleChange}
                  type="email"
                />
                <Field
                  label="City"
                  name="city"
                  value={formData.city}
                  editing={isEditing}
                  onChange={handleChange}
                />
                <Field
                  label="Country"
                  name="country"
                  value={formData.country}
                  editing={isEditing}
                  onChange={handleChange}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Preferred language</label>
                  {isEditing ? (
                    <select
                      name="preferred_language"
                      value={formData.preferred_language}
                      onChange={(e) => {
                        setFormData((prev) => ({ ...prev, preferred_language: e.target.value }));
                        setAppLanguage(e.target.value as AppLanguage);
                      }}
                      className="block w-full rounded-md border border-gray-300 px-3 py-2.5 text-base sm:text-sm min-h-[44px]"
                    >
                      {SUPPORTED_LANGUAGES.map((l) => (
                        <option key={l} value={l}>{LANGUAGE_NAMES[l]}</option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-sm text-gray-900">{LANGUAGE_NAMES[formData.preferred_language as AppLanguage] || formData.preferred_language}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">Used for the app and for order emails</p>
                </div>
                <div className="sm:col-span-2">
                  <Field
                    label="Address"
                    name="address"
                    value={formData.address}
                    editing={isEditing}
                    onChange={handleChange}
                    multiline
                  />
                </div>
                <div className="sm:col-span-2">
                  <Field
                    label="Bio"
                    name="bio"
                    value={formData.bio}
                    editing={isEditing}
                    onChange={handleChange}
                    multiline
                  />
                </div>
                <div className="sm:col-span-2">
                  <Field
                    label="Profile Picture URL"
                    name="profile_picture"
                    value={formData.profile_picture}
                    editing={isEditing}
                    onChange={handleChange}
                    placeholder="https://example.com/your-photo.jpg"
                  />
                </div>
              </div>
            </section>

            {/* Bank Details */}
            <section>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Bank Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="Bank Name"
                  name="bank_name"
                  value={formData.bank_name}
                  editing={isEditing}
                  onChange={handleChange}
                />
                <Field
                  label="Account Name"
                  name="bank_account_name"
                  value={formData.bank_account_name}
                  editing={isEditing}
                  onChange={handleChange}
                />
                <Field
                  label="Account Number"
                  name="bank_account_number"
                  value={formData.bank_account_number}
                  editing={isEditing}
                  onChange={handleChange}
                />
                <Field
                  label="Branch"
                  name="bank_branch"
                  value={formData.bank_branch}
                  editing={isEditing}
                  onChange={handleChange}
                />
              </div>
            </section>

            {/* Save button */}
            {isEditing && (
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 active:bg-gray-50 min-h-[44px] touch-manipulation"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md text-sm font-medium active:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] touch-manipulation flex items-center"
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
        <ChangePasswordCard />
      </div>
    </Layout>
  );
};

/* ----- Reusable field component ----- */

interface FieldProps {
  label: string;
  name: string;
  value: string;
  editing: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  type?: string;
  multiline?: boolean;
  placeholder?: string;
}

const Field: React.FC<FieldProps> = ({
  label,
  name,
  value,
  editing,
  onChange,
  type = 'text',
  multiline = false,
  placeholder = '',
}) => {
  if (!editing) {
    return (
      <div>
        <dt className="text-sm font-medium text-gray-500">{label}</dt>
        <dd className="mt-1 text-sm text-gray-900">{value || 'Not set'}</dd>
      </div>
    );
  }

  const className =
    'block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {multiline ? (
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          rows={3}
          className={className}
          placeholder={placeholder}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          className={className}
          placeholder={placeholder}
        />
      )}
    </div>
  );
};

export default Profile;
